// Turning dropped/selected files into InputFile objects: type checks,
// password prompts (decryption happens locally) and page counts.
import { fmt, formatBytes, t } from '../i18n';
import { sharedPdf } from '../lib/engines/pdfjs';
import { kindOf } from '../lib/files';
import { WrongPasswordError } from '../lib/pdf/errors';
import type { ImagesToPdfOptions } from '../lib/pdf/images';
import { looksEncrypted } from '../lib/pdf/sniff';
import type { ToolMeta } from '../registry';
import type { InputFile } from '../tools/types';
import { passwordDialog, toast } from '../ui/feedback';

const LARGE_FILE = 300 * 1024 * 1024;

let counter = 0;
const newId = () => `f${Date.now().toString(36)}${(counter++).toString(36)}`;

// pdf-lib is large: load it only when a file actually needs it.
const pdfDoc = () => import('../lib/pdf/doc');

async function unlockInteractively(name: string, bytes: Uint8Array): Promise<Uint8Array | null> {
  const { decryptPdf } = await pdfDoc();
  // Many "protected" PDFs only restrict printing/copying and open with an
  // empty user password.
  try {
    return await decryptPdf(bytes, '');
  } catch (err) {
    if (!(err instanceof WrongPasswordError)) throw err;
  }
  let wrong = false;
  for (;;) {
    const password = await passwordDialog(name, wrong);
    if (password === null) return null;
    try {
      return await decryptPdf(bytes, password);
    } catch (err) {
      if (!(err instanceof WrongPasswordError)) throw err;
      wrong = true;
    }
  }
}

async function countPages(bytes: Uint8Array): Promise<number> {
  try {
    return (await sharedPdf(bytes)).numPages;
  } catch {
    return (await pdfDoc()).pageCount(bytes);
  }
}

export async function ingestFiles(list: Iterable<File>, meta: ToolMeta): Promise<InputFile[]> {
  const d = t();
  const out: InputFile[] = [];
  for (const file of list) {
    const kind = kindOf(file.name, file.type);
    if (!kind || !meta.accept.includes(kind)) {
      toast(fmt(d.common.unsupported, { name: file.name }), 'warn');
      continue;
    }
    if (file.size > LARGE_FILE) toast(fmt(d.common.fileTooBig, { name: file.name, size: formatBytes(file.size) }), 'warn');
    let bytes: Uint8Array = new Uint8Array(await file.arrayBuffer());
    let wasEncrypted = false;
    let pages = 0;

    if (kind === 'pdf') {
      try {
        if (looksEncrypted(bytes)) {
          const decrypted = await unlockInteractively(file.name, bytes);
          if (!decrypted) {
            toast(fmt(d.errors.encryptedSkipped, { name: file.name }), 'warn');
            continue;
          }
          bytes = decrypted;
          wasEncrypted = true;
        }
        pages = await countPages(bytes);
      } catch {
        // The repair tool exists precisely for files nothing else can open.
        if (meta.id !== 'repair') {
          toast(fmt(d.errors.invalidPdf, { name: file.name }), 'error');
          continue;
        }
      }
    }

    out.push({
      id: newId(),
      name: file.name,
      size: file.size,
      kind,
      mime: file.type || (kind === 'pdf' ? 'application/pdf' : 'application/octet-stream'),
      bytes,
      pageCount: pages,
      wasEncrypted,
    });
  }
  return out;
}

export function fileFromBytes(bytes: Uint8Array, name: string, mime: string): File {
  return new File([bytes as BlobPart], name, { type: mime });
}

/** JPEG/PNG bytes ready for pdf-lib; other formats are converted to PNG. */
export async function embeddableImage(file: InputFile): Promise<{ bytes: Uint8Array; mime: 'image/jpeg' | 'image/png' }> {
  const head = file.bytes;
  const isJpeg = head[0] === 0xff && head[1] === 0xd8;
  const isPng = head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47;
  if (isJpeg) return { bytes: file.bytes, mime: 'image/jpeg' };
  if (isPng) return { bytes: file.bytes, mime: 'image/png' };
  try {
    const bitmap = await createImageBitmap(new Blob([file.bytes as BlobPart], { type: file.mime }));
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob = await canvas.convertToBlob({ type: 'image/png' });
    return { bytes: new Uint8Array(await blob.arrayBuffer()), mime: 'image/png' };
  } catch {
    throw new Error(fmt(t().errors.invalidImage, { name: file.name }));
  }
}

const pdfCache = new WeakMap<InputFile, Promise<Uint8Array>>();

/** The file as PDF bytes: PDFs as-is, images wrapped in a page (cached). */
export function asPdf(file: InputFile, opts: ImagesToPdfOptions = { pageSize: 'a4', orientation: 'auto', margin: 0 }): Promise<Uint8Array> {
  if (file.kind === 'pdf') return Promise.resolve(file.bytes);
  let p = pdfCache.get(file);
  if (!p) {
    p = Promise.all([embeddableImage(file), import('../lib/pdf/images')]).then(([img, { imagesToPdf }]) => imagesToPdf([img], opts));
    pdfCache.set(file, p);
  }
  return p;
}
